import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { renderStars } from '../../../optumus-core/src/utilities/rating.utils';

export interface Provider {
  npi: string;
  providerName: string;
  credentials: string | null;
  specialty: string;
  providerGroup: string | null;
  gender: 'F' | 'M';
  initials: string;
  distance: number | null;
  address: string;
  phone: string | null;
  acceptingNewPatients: boolean;
  visitModes: string[];
  telehealth: boolean;
  areasOfFocus: string[];
  languages: string[];
  insuranceAccepted: string[];
  rating: number | null;
  ratingCount: number | null;
  photoUrl: string | null;
  practiceId?: string;
  departmentId?: string;
  providerId?: string;
}

const StyledCard = styled('div', {
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.neutral.20',
  borderRadius: '$core.border-radius.lg',
  marginBottom: '$core.spacing.200',
  overflow: 'hidden',
  backgroundColor: '$core.color.neutral.0',
  shadowColor: '$core.color.neutral.100',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.08,
  shadowRadius: 14,
  elevation: 3,
});

const StyledCardHead = styled('div', {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  paddingVertical: '$core.spacing.200',
  borderBottomWidth: '$core.border-width.sm',
  borderBottomColor: '$core.color.neutral.20',
  backgroundColor: '$core.color.warm-white.10',
});

const StyledAvatar = styled('div', {
  width: '$core.sizing.600',
  height: '$core.sizing.600',
  borderRadius: '$core.border-radius.full',
  backgroundColor: '$core.color.blue.10',
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.blue.50',
  alignItems: 'center',
  justifyContent: 'center',
});

const StyledHeadInfo = styled('div', {
  flex: 1,
  gap: '$core.spacing.50',
});

const StyledCardBody = styled('div', {
  paddingHorizontal: '$core.spacing.200',
  paddingTop: '$core.spacing.150',
  paddingBottom: '$core.spacing.200',
  backgroundColor: '$core.color.neutral.0',
});

const StyledRatingRow = styled('div', {
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$core.spacing.100',
  marginBottom: '$core.spacing.150',
});

const StyledActions = styled('div', {
  flexDirection: 'row',
  gap: '$core.spacing.100',
});

const StyledPillBtnSolid = styled('button', {
  flex: 1,
  borderRadius: '$core.border-radius.full',
  minHeight: '$core.sizing.500',
  borderWidth: '$core.border-width.sm',
  backgroundColor: '$core.color.brand.80',
  borderColor: '$core.color.brand.80',
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: '$core.spacing.100',
});

const StyledPillBtnOutline = styled('button', {
  flex: 1,
  borderRadius: '$core.border-radius.full',
  minHeight: '$core.sizing.500',
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.brand.80',
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: '$core.spacing.100',
  backgroundColor: '$core.color.neutral.0',
});

const StyledPillBtnDisabled = styled('div', {
  flex: 1,
  borderRadius: '$core.border-radius.full',
  minHeight: '$core.sizing.500',
  borderWidth: '$core.border-width.sm',
  borderColor: '$core.color.neutral.30',
  backgroundColor: '$core.color.neutral.10',
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: '$core.spacing.100',
});

const StyledAvatarText = styled(Text, {
  fontWeight: '$core.font-weight.heavy',
});

const StyledProviderName = styled(Text, {
  fontWeight: '$core.font-weight.xbold',
  letterSpacing: -0.4,
  lineHeight: 20,
});

const StyledProviderRole = styled(Text, {
  fontWeight: '$core.font-weight.semibold',
});

const StyledDistance = styled(Text, {
  fontWeight: '$core.font-weight.semibold',
  marginTop: 2,
});

const StyledTagsText = styled(Text, {
  fontWeight: '$core.font-weight.semibold',
  marginBottom: '$core.spacing.100',
});

const StyledStarsText = styled(Text, {
  letterSpacing: 1,
  color: '$core.color.yellow.100',
});

const StyledRatingText = styled(Text, {
  fontWeight: '$core.font-weight.xbold',
});

const StyledSolidBtnText = styled(Text, {
  fontWeight: '$core.font-weight.xbold',
  color: '$core.color.neutral.0',
});

const StyledDisabledBtnText = styled(Text, {
  fontWeight: '$core.font-weight.bold',
  color: '$core.color.neutral.50',
});

const StyledOutlineBtnText = styled(Text, {
  fontWeight: '$core.font-weight.xbold',
});

interface ProviderCardProps {
  item: Provider;
  onScheduleButtonPress: () => void;
  onDirectionsButtonPress: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  item,
  onScheduleButtonPress,
  onDirectionsButtonPress,
}) => {
  const providerDisplayName = item.credentials
    ? `${item.providerName}, ${item.credentials}`
    : item.providerName;
  const ratingLabel =
    item.rating !== null && item.ratingCount !== null
      ? `${item.rating.toFixed(1)} (${item.ratingCount} ratings)`
      : 'No ratings yet';

  return (
    <StyledCard>
      <StyledCardHead>
        <StyledAvatar>
          <StyledAvatarText size="$core.font-size.p.80" color="$core.color.brand.80">
            {item.initials}
          </StyledAvatarText>
        </StyledAvatar>
        <StyledHeadInfo>
          <StyledProviderName size="$core.font-size.p.80" color="$core.color.brand.80">
            {providerDisplayName}
          </StyledProviderName>
          <StyledProviderRole size="$core.font-size.p.60" color="$core.color.brand.100">
            {item.specialty}
          </StyledProviderRole>
          <StyledDistance size="$core.font-size.p.60" color="$core.color.brand.80">
            {'\uD83D\uDCCD'}{' '}
            {item.distance !== null ? `${item.distance} mi away` : 'Distance unavailable'}
          </StyledDistance>
        </StyledHeadInfo>
      </StyledCardHead>

      <StyledCardBody>
        <StyledTagsText size="$core.font-size.p.60" color="$core.color.neutral.50">
          {item.areasOfFocus.join(' \u00B7 ')}
        </StyledTagsText>
        <StyledRatingRow>
          <StyledStarsText size="$core.font-size.p.80">
            {renderStars(item.rating ?? 0)}
          </StyledStarsText>
          <StyledRatingText size="$core.font-size.p.60" color="$core.color.brand.80">
            {ratingLabel}
          </StyledRatingText>
        </StyledRatingRow>
        <StyledActions>
          {item.acceptingNewPatients ? (
            <StyledPillBtnSolid
              accessibilityRole="button"
              accessibilityLabel={`Schedule with ${item.providerName}`}
              onPress={onScheduleButtonPress}
            >
              <StyledSolidBtnText size="$core.font-size.p.60">
                {'\u2726'} Schedule
              </StyledSolidBtnText>
            </StyledPillBtnSolid>
          ) : (
            <StyledPillBtnDisabled accessibilityLabel="Scheduling not available">
              <StyledDisabledBtnText size="$core.font-size.p.60">
                {'\u260E\uFE0E'} Call to schedule
              </StyledDisabledBtnText>
            </StyledPillBtnDisabled>
          )}
          <StyledPillBtnOutline
            accessibilityRole="button"
            accessibilityLabel={`Get directions to ${item.providerName}`}
            onPress={onDirectionsButtonPress}
          >
            <StyledOutlineBtnText size="$core.font-size.p.60" color="$core.color.brand.80">
              {'\u27A4'} Get directions
            </StyledOutlineBtnText>
          </StyledPillBtnOutline>
        </StyledActions>
      </StyledCardBody>
    </StyledCard>
  );
};

export default ProviderCard;
